import {useEffect, useState} from 'react'
import {Calendar, Pencil, Plus, Trash, AlertCircle} from 'lucide-react'
import {Text} from '@/components/tailwind/text'
import {Button} from '@/components/tailwind/button'
import {Badge} from '@/components/tailwind/badge'
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from '@/components/tailwind/table'
import {Dialog, DialogBody, DialogTitle} from '@/components/tailwind/dialog'
import {PricingScheme} from '@/models/pricing'
import {getPricingSchemes, deletePricingScheme} from '@/services/pricing-services'
import {PricingSchemeForm} from '@/components/pricing/pricing-scheme-form'
import {usePricingTranslation, useCommonTranslation, useAdminTranslation} from "@/i18n/hooks"
import {ConfirmationDialog, useConfirmationDialog} from '@/components/confirmation-dialog'
import {Card, CardContent, CardHeader, CardTitle} from "@/components/ui/card"
import LoadingSpinner from "@/components/loader-spinner"
import {useAuth} from "@/context/use-auth"
import {useBillingUnitLabel} from "@/hooks/use-billing-unit-label"
import log from '@/lib/logger'

interface SectionPricingConfigurationProps {
    isMissing?: boolean;
}

export function SectionPricingConfiguration({ isMissing = false }: SectionPricingConfigurationProps) {
    const { t } = usePricingTranslation()
    const { t: tCommon } = useCommonTranslation()
    const { t: tAdmin } = useAdminTranslation()
    const {token} = useAuth()
    const {unitLabel} = useBillingUnitLabel()
    const [schemes, setSchemes] = useState<PricingScheme[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [isFormOpen, setIsFormOpen] = useState(false)
    const [editingScheme, setEditingScheme] = useState<PricingScheme | null>(null)
    const [schemeToDelete, setSchemeToDelete] = useState<PricingScheme | null>(null)
    const confirmationDialog = useConfirmationDialog()

    useEffect(() => {
        if (token) {
            loadSchemes()
        }
    }, [token])

    async function loadSchemes() {
        try {
            setLoading(true)
            setError(null)
            const data = await getPricingSchemes(token)
            setSchemes(data || [])
        } catch (err: any) {
            log.error('Error loading pricing schemes:', err)

            if (err.status === 404) {
                setSchemes([])
                setError(null)
            } else if (err.status === 403 || err.status === 401) {
                setError('Keine Berechtigung für den Zugriff auf Preisschemata. Nur Administratoren können Preisschemata verwalten.')
            } else {
                setError(t('messages.loadError'))
            }
        } finally {
            setLoading(false)
        }
    }

    function handleCreateScheme() {
        setEditingScheme(null)
        setIsFormOpen(true)
    }

    function handleEditScheme(scheme: PricingScheme) {
        setEditingScheme(scheme)
        setIsFormOpen(true)
    }

    function handleDeleteScheme(scheme: PricingScheme) {
        setSchemeToDelete(scheme)
        confirmationDialog.openWithConfig({
            title: "deleteConfirmation.pricingScheme.title",
            description: "deleteConfirmation.pricingScheme.description",
            descriptionValues: {
                schemeName: scheme.name
            },
            cancelButton: {
                text: "deleteConfirmation.buttons.cancel.text",
                ariaLabel: "deleteConfirmation.buttons.cancel.ariaLabel"
            },
            confirmButton: {
                text: "deleteConfirmation.buttons.submit.text",
                ariaLabel: "deleteConfirmation.buttons.submit.ariaLabel",
                color: "red"
            }
        })
    }

    async function confirmDelete() {
        if (!schemeToDelete) return

        try {
            await deletePricingScheme(token, schemeToDelete.id)
            await loadSchemes()
            setSchemeToDelete(null)
        } catch (err) {
            setError(t('messages.deleteError'))
            log.error('Error deleting pricing scheme:', err)
        }
    }

    function handleFormSuccess() {
        setIsFormOpen(false)
        setEditingScheme(null)
        loadSchemes()
    }

    function formatPrice(price: number): string {
        return new Intl.NumberFormat('de-DE', {
            style: 'currency',
            currency: 'EUR'
        }).format(price)
    }

    function formatDate(dateString: string): string {
        return new Intl.DateTimeFormat('de-DE', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        }).format(new Date(dateString))
    }

    function getSchemeStatus(scheme: PricingScheme): 'active' | 'future' | 'expired' {
        const now = new Date()
        const validFrom = new Date(scheme.validFrom)

        if (validFrom > now) return 'future'

        const activeSchemes = schemes.filter(s => new Date(s.validFrom) <= now)
        if (activeSchemes.length === 0) return 'future'

        const mostRecentScheme = activeSchemes.reduce((latest, current) =>
            new Date(current.validFrom) > new Date(latest.validFrom) ? current : latest
        )

        return mostRecentScheme.id === scheme.id ? 'active' : 'expired'
    }

    function canEditScheme(_scheme: PricingScheme): boolean {
        return true
    }

    function canDeleteScheme(scheme: PricingScheme): boolean {
        return getSchemeStatus(scheme) !== 'active'
    }

    function getValidUntil(scheme: PricingScheme): string | null {
        // Sortiere alle Schemes nach validFrom Datum
        const sortedSchemes = [...schemes].sort((a, b) => 
            new Date(a.validFrom).getTime() - new Date(b.validFrom).getTime()
        )
        
        // Finde das aktuelle Schema im sortierten Array
        const currentIndex = sortedSchemes.findIndex(s => s.id === scheme.id)
        
        // Wenn es ein nachfolgendes Schema gibt, ist validUntil der Tag vor dessen validFrom
        if (currentIndex !== -1 && currentIndex < sortedSchemes.length - 1) {
            const nextScheme = sortedSchemes[currentIndex + 1]
            const nextValidFrom = new Date(nextScheme.validFrom)
            
            // Einen Tag abziehen
            const validUntil = new Date(nextValidFrom)
            validUntil.setDate(validUntil.getDate() - 1)
            
            return validUntil.toISOString()
        }
        
        // Kein nachfolgendes Schema gefunden
        return null
    }

    if (loading) {
        return (
            <Card className="p-6 mt-6 border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950">
                <div className="flex items-center justify-center h-64">
                    <LoadingSpinner />
                    <Text className="ml-3">{t('loading')}</Text>
                </div>
            </Card>
        )
    }

    if (error) {
        return (
            <Card className="p-6 mt-6 border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950">
                <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg p-4">
                    <Text className="text-red-700 dark:text-red-300">{error}</Text>
                    <Button
                        className="mt-4"
                        onClick={() => {
                            setError(null)
                            loadSchemes()
                        }}
                    >
                        {tCommon('buttons.retry.text')}
                    </Button>
                </div>
            </Card>
        )
    }

    const hasNoSchemes = schemes.length === 0

    return (
        <>
            <Card className="p-6 mt-6 border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950">
                <CardHeader>
                    <div className="flex justify-between items-start">
                        <div>
                            <CardTitle className="text-xl font-bold flex items-center gap-2">
                                {t('title')}
                                {isMissing && (
                                    <span 
                                        title={tAdmin("adminSettings.missingConfiguration.tooltip")}
                                        aria-label={tAdmin("adminSettings.missingConfiguration.ariaLabel")}
                                    >
                                        <AlertCircle className="h-5 w-5 text-amber-500 dark:text-amber-400" />
                                    </span>
                                )}
                            </CardTitle>
                            <Text className="mt-1 text-sm text-muted-foreground">{t('subtitle')}</Text>
                        </div>
                        <Button outline onClick={handleCreateScheme} data-testid="add-price-config-button">
                            <Plus className="h-5 w-5 mr-2" data-slot="icon" />
                            {t('createScheme')}
                        </Button>
                    </div>
                </CardHeader>

                <CardContent>
                    {hasNoSchemes ? (
                        <div className="bg-blue-50 dark:bg-blue-950 border-l-4 border-blue-400 dark:border-blue-500 p-4 rounded">
                            <Text className="text-blue-700 dark:text-blue-300">
                                {t('noConfigurationYet')}
                            </Text>
                        </div>
                    ) : (
                            <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableHeader>{t('form.name')}</TableHeader>
                                                <TableHeader>{t('form.description')}</TableHeader>
                                                <TableHeader>{t('form.validFrom')}</TableHeader>
                                                <TableHeader>{t('form.validUntil')}</TableHeader>
                                                <TableHeader>{t('table.status')}</TableHeader>
                                                <TableHeader>{t('table.tiers')}</TableHeader>
                                                <TableHeader className="text-right">{t('table.actions')}</TableHeader>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {schemes.map((scheme) => {
                                                const status = getSchemeStatus(scheme)
                                                const validUntil = getValidUntil(scheme)
                                                return (
                                                    <TableRow key={scheme.id}>
                                                        <TableCell className="font-medium">
                                                            <div className="flex items-center gap-2">
                                                                {scheme.name}
                                                            </div>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Text className="text-sm">{scheme.description || '-'}</Text>
                                                        </TableCell>
                                                        <TableCell>
                                                            <div className="flex items-center gap-1">
                                                                <Calendar className="h-4 w-4 text-zinc-500" />
                                                                {formatDate(scheme.validFrom)}
                                                            </div>
                                                        </TableCell>
                                                        <TableCell>
                                                            {validUntil ? (
                                                                <div className="flex items-center gap-1">
                                                                    <Calendar className="h-4 w-4 text-zinc-500" />
                                                                    {formatDate(validUntil)}
                                                                </div>
                                                            ) : (
                                                                <Text className="text-gray-500 dark:text-gray-400">-</Text>
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Badge
                                                                color={
                                                                    status === 'active' ? 'green' :
                                                                        status === 'future' ? 'blue' : 'zinc'
                                                                }
                                                            >
                                                                {t(`status.${status}`)}
                                                            </Badge>
                                                        </TableCell>
                                                        <TableCell>
                                                            <div className="space-y-1">
                                                                {scheme.tiers.map((tier) => (
                                                                    <div key={tier.id} className="text-sm">
                                                                        {tier.fromUnit}
                                                                        {tier.toUnit ? `-${tier.toUnit}` : '+'} {t('units.hours', {unit: unitLabel})}
                                                                        : {formatPrice(tier.pricePerUnit)}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </TableCell>
                                                        <TableCell>
                                                            <div className="flex justify-end gap-2">
                                                                <Button
                                                                    plain
                                                                    onClick={() => handleEditScheme(scheme)}
                                                                    disabled={!canEditScheme(scheme)}
                                                                    title={!canEditScheme(scheme) ? t('messages.cannotEditActive') : undefined}
                                                                >
                                                                    <Pencil className="h-4 w-4" />
                                                                </Button>
                                                                <Button
                                                                    plain
                                                                    onClick={() => handleDeleteScheme(scheme)}
                                                                    disabled={!canDeleteScheme(scheme)}
                                                                    title={!canDeleteScheme(scheme) ? t('messages.cannotDeleteActive') : undefined}
                                                                    data-testid="delete-price-config"
                                                                >
                                                                    <Trash className="h-4 w-4 text-red-500" />
                                                                </Button>
                                                            </div>
                                                        </TableCell>
                                                    </TableRow>
                                                )
                                            })}
                                        </TableBody>
                                    </Table>
                    )}
                </CardContent>
            </Card>

            {/* Form Dialog */}
            <Dialog open={isFormOpen} onClose={() => setIsFormOpen(false)} size="2xl">
                <DialogTitle>
                    {editingScheme ? t('editScheme') : t('createScheme')}
                </DialogTitle>
                <DialogBody>
                    <PricingSchemeForm
                        initialData={editingScheme}
                        onSuccess={handleFormSuccess}
                        onCancel={() => setIsFormOpen(false)}
                    />
                </DialogBody>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            {confirmationDialog.dialogProps && (
                <ConfirmationDialog
                    {...confirmationDialog.dialogProps}
                    onConfirm={confirmDelete}
                    namespace="common"
                />
            )}
        </>
    )
}